import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Scale, ShieldCheck, AlertTriangle, Download, CheckCircle2, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// AgreementSign — public route /agreement/:token
// Each party reaches their unique URL, reviews terms, and confirms.
// ---------------------------------------------------------------------------

export default function AgreementSign() {
  const { token } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/agreement/${token}`);
      setAgreement(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Agreement not found or link expired");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await axios.post(`${API}/agreement/${token}/confirm`, { confirm: true });
      setAgreement(data);
      toast.success("Terms confirmed. Thank you.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setConfirming(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${API}/agreement/${token}/pdf`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <div className="text-sm text-zinc-500">Loading agreement…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="agreement-error">
          <AlertTriangle className="h-8 w-8 text-amber-700 mx-auto" />
          <h1 className="mt-3 font-serif text-3xl text-zinc-900">Agreement link not available</h1>
          <p className="mt-3 text-sm text-zinc-600">{error}</p>
          <Link to="/" className="mt-6 inline-block text-sm text-zinc-700 hover:text-zinc-900 underline">Back to home</Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const terms = agreement?.terms || {};
  const confirmations = agreement?.confirmations || {};
  const me = agreement?.viewing_party;
  const myName = terms[`${me}_name`] || (me === "party_a" ? "Party A" : "Party B");
  const otherKey = me === "party_a" ? "party_b" : "party_a";
  const otherName = terms[`${otherKey}_name`] || (otherKey === "party_a" ? "Party A" : "Party B");
  const myConfirmed = !!confirmations[me]?.confirmed_at;
  const otherConfirmed = !!confirmations[otherKey]?.confirmed_at;
  const payerName = terms.paying_party === "a" ? terms.party_a_name : terms.party_b_name;
  const recipientName = terms.paying_party === "a" ? terms.party_b_name : terms.party_a_name;

  return (
    <div className="min-h-screen bg-zinc-50">
      <SiteHeader />
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-14" data-testid="agreement-sign-page">
        <div className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-amber-700">
          <Scale className="h-3.5 w-3.5" /> Maintenance Agreement · {agreement?.matter_reference || agreement?.matter_id}
        </div>
        <h1 className="mt-3 font-serif text-4xl lg:text-5xl tracking-tight text-zinc-900">Review and confirm</h1>
        <p className="mt-3 text-zinc-600">
          You are signing in as <strong>{myName}</strong>. Please review the proposed terms below and confirm if you agree.
        </p>

        {/* Status strip */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200" data-testid="agreement-status">
          <StatusCell label={`${terms.party_a_name || "Party A"}`}
                       value={confirmations.party_a?.confirmed_at ? "Confirmed" : "Pending"}
                       confirmed={!!confirmations.party_a?.confirmed_at}
                       at={confirmations.party_a?.confirmed_at}
                       testid="status-party-a" />
          <StatusCell label={`${terms.party_b_name || "Party B"}`}
                       value={confirmations.party_b?.confirmed_at ? "Confirmed" : "Pending"}
                       confirmed={!!confirmations.party_b?.confirmed_at}
                       at={confirmations.party_b?.confirmed_at}
                       testid="status-party-b" />
          <StatusCell label="Overall status"
                       value={agreement?.status?.replace(/_/g, " ")}
                       confirmed={agreement?.status === "fully_confirmed"}
                       testid="status-overall" />
        </div>

        {/* Court-order warning — front and centre */}
        <div className="mt-6 bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="agreement-court-order-note">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">{agreement?.court_order_note}</div>
            <div className="text-xs text-amber-700 mt-0.5">{agreement?.agreement_note}</div>
          </div>
        </div>

        {/* Terms */}
        <section className="mt-8 border border-zinc-200 bg-white p-6 lg:p-8 space-y-6" data-testid="agreement-terms">
          <TermRow label="Parties" value={`${terms.party_a_name} and ${terms.party_b_name}`} />
          <TermRow label="Children"
                     value={(terms.children || []).map((c) => `${c.name}${c.age ? ` (age ${c.age})` : ""}`).join(", ") || "—"} />
          <TermRow label="Monthly contribution"
                     value={`${payerName} pays ${recipientName} R ${(terms.monthly_amount || 0).toLocaleString("en-ZA")} / month`} emphasis />
          <TermRow label="Payment day" value={`On or before the ${terms.payment_due_day || 1} of each month`} />
          <TermRow label="Start date" value={terms.start_date || "—"} />
          <TermRow label="Payment method" value={(terms.payment_method || "eft").toUpperCase()} />
          <TermRow label="Annual escalation" value={terms.escalation_pct ? `${terms.escalation_pct}% per annum` : "None"} />
          <TermRow label="Duration"
                     value={
                       terms.duration === "until_majority" ? "Until each child attains majority or is self-supporting"
                       : terms.duration === "fixed_term"   ? `Until ${terms.fixed_term_end || "—"}`
                       : terms.duration === "indefinite"   ? "Indefinite (until varied by agreement / court order)"
                       : "—"
                     } />
          {terms.additional_terms && (
            <TermRow label="Additional terms" value={terms.additional_terms} />
          )}
        </section>

        <div className="mt-4 text-xs text-zinc-500 italic" data-testid="agreement-disclaimer">{agreement?.disclaimer}</div>

        {/* Action row */}
        <div className="mt-8 flex flex-wrap gap-3" data-testid="agreement-actions">
          {!myConfirmed ? (
            <button type="button" onClick={confirm} disabled={confirming}
                       className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-700 disabled:opacity-50"
                       data-testid="agreement-confirm-btn">
              <ShieldCheck className="h-4 w-4" /> {confirming ? "Confirming…" : "I confirm these terms"}
            </button>
          ) : (
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-5 py-3 text-sm text-emerald-900" data-testid="agreement-confirmed-state">
              <CheckCircle2 className="h-4 w-4" /> You confirmed on {new Date(confirmations[me].confirmed_at).toLocaleString()}.
              {!otherConfirmed && (
                <span className="text-emerald-700"> Awaiting {otherName}.</span>
              )}
              {otherConfirmed && (
                <span className="text-emerald-700"> Both parties have confirmed.</span>
              )}
            </div>
          )}
          <button type="button" onClick={downloadPdf}
                     className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-4 py-3 text-sm font-medium text-zinc-800 hover:border-zinc-500"
                     data-testid="agreement-download-pdf">
            <Download className="h-4 w-4" /> Download PDF copy
          </button>
        </div>

        {agreement?.status === "fully_confirmed" && (
          <div className="mt-8 border border-emerald-200 bg-emerald-50 p-6" data-testid="agreement-fully-confirmed-note">
            <div className="text-xs tracking-[0.18em] uppercase text-emerald-700">Both parties confirmed</div>
            <div className="mt-1 font-medium text-emerald-900">
              EH Inc will now prepare the agreement for the Maintenance Court to make it an order. <ArrowRight className="inline h-4 w-4" />
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}


function TermRow({ label, value, emphasis }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6">
      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 sm:w-44 flex-shrink-0">{label}</div>
      <div className={`text-sm ${emphasis ? "font-medium text-zinc-900" : "text-zinc-800"} leading-relaxed whitespace-pre-line`}>{value}</div>
    </div>
  );
}

function StatusCell({ label, value, confirmed, at, testid }) {
  return (
    <div className={`p-4 ${confirmed ? "bg-emerald-50" : "bg-white"}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`mt-1 text-sm font-medium ${confirmed ? "text-emerald-700" : "text-zinc-700"}`}>{value}</div>
      {at && <div className="text-[10px] text-zinc-500 mt-0.5">{new Date(at).toLocaleString()}</div>}
    </div>
  );
}
